import React from 'react';
import { FiZap } from 'react-icons/fi';

import { useTranslation } from '../../../contexts/TranslationContext';
import { TranslationKeys } from '../../../translations';
import { Game, PrimaryOddsItem } from '../../../types/interfaces';
import { translateEvent } from '../../../utils/translateEvent';
import './GamePanel.scss';

const PALETTE = ['#58C9FA', '#f85149', '#3fb950', '#d29922', '#a78bfa', '#fb923c'];

function getAccentColor(name?: string): string {
  if (!name) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function formatGameDate(dt: string, t: TranslationKeys): string {
  try {
    const d = new Date(dt);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tom = new Date(today);
    tom.setDate(today.getDate() + 1);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const time = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    if (day.getTime() === today.getTime()) return `${t.common.today} · ${time}`;
    if (day.getTime() === tom.getTime()) return `${t.common.tomorrow} · ${time}`;
    return d.toLocaleDateString('en-US', { weekday: 'short' }) + ` · ${time}`;
  } catch {
    return dt;
  }
}

function parseOdds(primary?: PrimaryOddsItem[]) {
  if (!primary || primary.length === 0) return null;
  if (primary.length >= 3) {
    return {
      home: primary[0].odds.toFixed(2),
      draw: primary[1].odds.toFixed(2),
      away: primary[2].odds.toFixed(2),
    };
  }
  return {
    home: primary[0].odds.toFixed(2),
    draw: null,
    away: primary[1]?.odds.toFixed(2) ?? null,
  };
}

function leagueAbbr(name: string): string {
  if (name.length <= 5) return name;
  const initials = name.split(/\s+/).map(w => w[0]).join('').toUpperCase();
  return initials.slice(0, 4);
}

export interface GamePanelProps {
  game: Game;
  featured?: boolean;
  compact?: boolean;
  isLive?: boolean;
  primaryOdds?: PrimaryOddsItem[];
  numberOfPicksLive?: number;
  numberOfAnalyses?: number;
  colorId?: number;
  onClick?: () => void;
}

const GamePanel: React.FC<GamePanelProps> = ({
  game,
  featured = false,
  compact = false,
  isLive = false,
  primaryOdds,
  numberOfPicksLive,
  numberOfAnalyses,
  onClick,
}) => {
  const { t } = useTranslation();
  const leagueName = game.league?.name ?? '';
  const translatedLeagueName = leagueName ? translateEvent(leagueName, t.eventsDictionary) : '';
  const homeTeam = translateEvent(game.home_team, t.eventsDictionary);
  const awayTeam = translateEvent(game.away_team, t.eventsDictionary);
  const accent = getAccentColor(leagueName);
  const date = formatGameDate(game.datetime, t);
  const odds = parseOdds(primaryOdds ?? game.primary_odds);
  const cssVars = { '--accent': accent } as React.CSSProperties;

  if (compact) {
    return (
      <div className="game-panel game-panel--compact" style={cssVars} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
        <div className="game-panel__compact-badge">
          {leagueAbbr(translatedLeagueName) || '?'}
        </div>
        <div className="game-panel__compact-info">
          <div className="game-panel__compact-teams">
            <span>{homeTeam}</span>
            <span className="game-panel__compact-vs"> {t.common.vs} </span>
            <span>{awayTeam}</span>
          </div>
          <div className="game-panel__compact-meta">
            {date}{numberOfPicksLive != null ? ` · ${t.feed.picksCount.replace('{count}', String(numberOfPicksLive))}` : ''}
          </div>
        </div>
        {odds && (
          <div className="game-panel__compact-odds">
            {(
              [
                [odds.home, '1'],
                odds.draw ? [odds.draw, 'X'] : null,
                odds.away ? [odds.away, '2'] : null,
              ] as ([string, string] | null)[]
            )
              .filter((x): x is [string, string] => x !== null)
              .map(([val, lbl]) => (
                <div key={lbl} className="game-panel__compact-odds-item">
                  <span className="game-panel__compact-odds-label">{lbl}</span>
                  <span className="game-panel__compact-odds-val">{val}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  }

  if (featured) {
    return (
      <div className={`game-panel game-panel--featured${isLive ? ' game-panel--live' : ''}`} style={cssVars} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
        <div className="game-panel__accent-line" />
        <div className="game-panel__ghost" aria-hidden="true">
          <span className="game-panel__ghost-home">{homeTeam}</span>
          <span className="game-panel__ghost-away">{awayTeam}</span>
        </div>

        <div className="game-panel__feat-header">
          <div className="game-panel__meta">
            <span className="game-panel__featured-badge">{t.feed.featured} · {translatedLeagueName}</span>
            <span className="game-panel__date">{date}</span>
            {isLive && (
              <span className="game-panel__live-badge">
                <FiZap size={9} /> {t.common.live}
              </span>
            )}
          </div>
          {numberOfPicksLive != null && (
            <div className="game-panel__live">
              <span className="game-panel__live-dot" />
              <span>{t.feed.picksLive.replace('{count}', String(numberOfPicksLive))}</span>
            </div>
          )}
        </div>

        <div className="game-panel__feat-matchup">
          <div className="game-panel__side game-panel__side--home">
            <div className="game-panel__side-pip game-panel__side-pip--accent" />
            <span className="game-panel__team-name game-panel__team-name--featured-home">
              {homeTeam}
            </span>
            {odds && (
              <div className="game-panel__big-odds">
                <span className="game-panel__big-odds-val">{odds.home}</span>
                <span className="game-panel__big-odds-label">{t.feed.home}</span>
              </div>
            )}
          </div>

          <div className="game-panel__versus">
            <span className="game-panel__versus-text">{t.feed.versus}</span>
            <div className="game-panel__versus-rule" />
            {odds?.draw && (
              <div className="game-panel__draw-pill">X · {odds.draw}</div>
            )}
          </div>

          <div className="game-panel__side game-panel__side--away">
            <div className="game-panel__side-pip" />
            <span className="game-panel__team-name game-panel__team-name--featured-away">
              {awayTeam}
            </span>
            {odds && odds.away && (
              <div className="game-panel__big-odds game-panel__big-odds--away">
                <span className="game-panel__big-odds-label">{t.feed.away}</span>
                <span className="game-panel__big-odds-val game-panel__big-odds-val--muted">
                  {odds.away}
                </span>
              </div>
            )}
          </div>
        </div>

        {odds && (
          <div className="game-panel__odds-strip">
            {(
              [
                [t.feed.homeStrip, odds.home, true],
                odds.draw ? [t.feed.drawStrip, odds.draw, false] : null,
                odds.away ? [t.feed.awayStrip, odds.away, false] : null,
              ] as ([string, string, boolean] | null)[]
            )
              .filter((x): x is [string, string, boolean] => x !== null)
              .map(([lbl, val, isAccent]) => (
                <div
                  key={lbl}
                  className={`game-panel__strip-item${isAccent ? ' game-panel__strip-item--accent' : ''}`}
                >
                  <span className="game-panel__strip-label">{lbl}</span>
                  <span className="game-panel__strip-val">{val}</span>
                </div>
              ))}
            <div className="game-panel__strip-cta">
              {numberOfAnalyses != null && (
                <span className="game-panel__strip-experts">
                  {t.feed.expertAnalyses.replace('{count}', String(numberOfAnalyses))}
                </span>
              )}
              <span className="game-panel__view-picks">{t.feed.viewPicks}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`game-panel game-panel--normal${isLive ? ' game-panel--live' : ''}`} style={cssVars} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
      <div className="game-panel__accent-line" />
      <div className="game-panel__ghost" aria-hidden="true">
        <span className="game-panel__ghost-home">{homeTeam}</span>
        <span className="game-panel__ghost-away">{awayTeam}</span>
      </div>

      <div className="game-panel__body">
        <div className="game-panel__header">
          <div className="game-panel__meta">
            <span className="game-panel__league">{translatedLeagueName}</span>
            <span className="game-panel__date">{date}</span>
            {isLive && (
              <span className="game-panel__live-badge">
                <FiZap size={9} /> {t.common.live}
              </span>
            )}
          </div>
          {(numberOfPicksLive != null || numberOfAnalyses != null) && (
            <span className="game-panel__stats">
              {[
                numberOfPicksLive != null ? t.feed.picksCount.replace('{count}', String(numberOfPicksLive)) : null,
                numberOfAnalyses != null ? t.feed.expertsCount.replace('{count}', String(numberOfAnalyses)) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </div>

        <div className="game-panel__matchup">
          <div className="game-panel__team-row">
            <div className="game-panel__team-bar game-panel__team-bar--accent" />
            <span className="game-panel__team-name game-panel__team-name--home">
              {homeTeam}
            </span>
          </div>
          <div className="game-panel__vs-row">
            <span className="game-panel__vs-label">{t.common.vs}</span>
          </div>
          <div className="game-panel__team-row">
            <div className="game-panel__team-bar" />
            <span className="game-panel__team-name">{awayTeam}</span>
          </div>
        </div>

        {odds && (
          <div className="game-panel__odds">
            {(
              [
                ['1', odds.home, true],
                odds.draw ? ['X', odds.draw, false] : null,
                odds.away ? ['2', odds.away, false] : null,
              ] as ([string, string, boolean] | null)[]
            )
              .filter((x): x is [string, string, boolean] => x !== null)
              .map(([lbl, val, isAccent]) => (
                <div
                  key={lbl}
                  className={`game-panel__odds-item${isAccent ? ' game-panel__odds-item--accent' : ''}`}
                >
                  <span className="game-panel__odds-label">{lbl}</span>
                  <span className="game-panel__odds-val">{val}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GamePanel;
